/**
 * Trainova AI — Platform Tenant Details Page
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { admin } from '../../../services/api';
import { Tenant } from '../types';
import { useTenantUsers } from '../hooks/useTenantUsers';
import { UserTable } from '../components/UserTable';
import { TenantForm } from '../components/TenantForm';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { useTranslation } from '../../../i18n';

export function TenantDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    users,
    isLoading: usersLoading,
    updateUserRole,
    updateUserStatus,
  } = useTenantUsers(id);

  const fetchTenant = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await admin.tenants.get(id);
      if (res.success && res.data) {
        setTenant(res.data);
      } else {
        setError(res.error?.message || 'Failed to load organization details.');
      }
    } catch {
      setError('An error occurred while fetching organization details.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const handleEditSubmit = async (data: {
    name: string;
    primaryColor: string;
    slug: string;
    loginLogoUrl?: string | null;
    topNavLogoUrl?: string | null;
    directLoginUrl?: string | null;
    learnerCanViewOwnResultReport?: boolean;
    videoAnalysisEnabled?: boolean;
    openAiVoiceEnabled?: boolean;
    openAiVoiceEntitled?: boolean;
    mobileNavConfig?: any;
  }) => {
    if (!id) return;
    setIsUpdating(true);
    setError(null);
    try {
      const res = await admin.tenants.update(id, {
        name: data.name,
        primaryColor: data.primaryColor,
        loginLogoUrl: data.loginLogoUrl,
        topNavLogoUrl: data.topNavLogoUrl,
        directLoginUrl: data.directLoginUrl,
        learnerCanViewOwnResultReport: data.learnerCanViewOwnResultReport,
        videoAnalysisEnabled: data.videoAnalysisEnabled,
        openAiVoiceEnabled: data.openAiVoiceEnabled,
        openAiVoiceEntitled: data.openAiVoiceEntitled,
        mobileNavConfig: data.mobileNavConfig,
      });
      if (res.success) {
        setShowEditModal(false);
        await fetchTenant();
      } else {
        setError(res.error?.message || 'Failed to update organization details.');
      }
    } catch {
      setError('An error occurred while updating organization.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading-state">
        <div className="dashboard-spinner" />
        <p>Loading organization details…</p>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="admin-page">
        <div className="admin-alert admin-alert--error">{error || 'Organization not found.'}</div>
        <Link to="/admin/tenants" className="admin-btn admin-btn--secondary">
          ← Back to Organizations
        </Link>
      </div>
    );
  }

  const effectiveLoginUrl = tenant.directLoginUrl
    ? (tenant.directLoginUrl.startsWith('http') ? tenant.directLoginUrl : `${window.location.origin}${tenant.directLoginUrl.startsWith('/') ? '' : '/'}${tenant.directLoginUrl}`)
    : `${window.location.origin}/login/${tenant.slug}`;

  const learnerCanViewReports = tenant.governancePolicy?.learnerCanViewOwnResultReport ?? true;

  return (
    <div className="admin-page" style={{ display: 'grid', gap: '20px' }}>
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Organizations', to: '/admin/tenants' },
          { label: tenant.name },
        ]}
        icon={
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: tenant.primaryColor || '#6366F1',
              color: '#FFFFFF',
              fontWeight: 800,
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            {tenant.name.substring(0, 2).toUpperCase()}
          </span>
        }
        title={tenant.name}
        badge={{
          label: tenant.status || 'ACTIVE',
          bg: tenant.status === 'SUSPENDED' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.25)',
          color: tenant.status === 'SUSPENDED' ? '#FCA5A5' : '#6EE7B7',
          border: tenant.status === 'SUSPENDED' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
        }}
        description={
          <span>
            Organization Slug: <code style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '4px', color: '#93C5FD', fontFamily: 'monospace' }}>{tenant.slug}</code> • Organization ID: <code style={{ color: '#E2E8F0', fontFamily: 'monospace' }}>{tenant.id}</code>
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="admin-btn admin-btn--primary"
            style={{
              background: '#2563EB',
              color: '#FFFFFF',
              border: 'none',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
            }}
          >
            ✏️ Edit Organization & Branding
          </button>
        }
      />

      {/* ============================================================== */}
      {/* WORKSTREAM 2 & 3: Learner Access & Branding Card */}
      {/* ============================================================== */}
      <div
        className="admin-card"
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: 'var(--display)', fontSize: '16px', fontWeight: 700, color: 'var(--navy)' }}>
              Learner Access & Organization Branding
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Tenant-specific login URLs, visual identity assets, and learner result visibility controls.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="admin-btn admin-btn--secondary"
            style={{ fontSize: '12.5px', height: '34px', padding: '0 12px' }}
          >
            Configure
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px' }}>
          {/* Direct Login URL */}
          <div style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Direct Learner Login URL
            </span>
            <div style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--blue)', fontWeight: 600, marginTop: '4px', wordBreak: 'break-all' }}>
              {effectiveLoginUrl}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                style={{ height: '30px', fontSize: '11.5px', padding: '0 10px', cursor: 'pointer' }}
                onClick={() => {
                  navigator.clipboard.writeText(effectiveLoginUrl);
                  alert(`Copied direct login URL: ${effectiveLoginUrl}`);
                }}
              >
                📋 Copy Link
              </button>
              <a
                href={effectiveLoginUrl}
                target="_blank"
                rel="noreferrer"
                className="admin-btn admin-btn--secondary"
                style={{ height: '30px', fontSize: '11.5px', padding: '0 10px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
              >
                🔗 Test Login
              </a>
            </div>
          </div>

          {/* Login Page Logo */}
          <div style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Login Page Logo
            </span>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {tenant.loginLogoUrl ? (
                <img
                  src={tenant.loginLogoUrl}
                  alt="Login Logo"
                  style={{ maxHeight: '36px', maxWidth: '140px', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
              ) : (
                <span style={{ fontSize: '12.5px', color: 'var(--muted)', fontStyle: 'italic' }}>
                  Default platform logo
                </span>
              )}
            </div>
          </div>

          {/* Top Navigation Logo */}
          <div style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Top Navigation Logo
            </span>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {tenant.topNavLogoUrl ? (
                <img
                  src={tenant.topNavLogoUrl}
                  alt="Top Nav Logo"
                  style={{ maxHeight: '32px', maxWidth: '140px', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                />
              ) : (
                <span style={{ fontSize: '12.5px', color: 'var(--muted)', fontStyle: 'italic' }}>
                  Default platform logo
                </span>
              )}
            </div>
          </div>

          {/* Learner Report ACL */}
          <div style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Learner Report Visibility (ACL)
            </span>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: learnerCanViewReports ? 'var(--ok-soft)' : 'var(--warn-soft)',
                  color: learnerCanViewReports ? 'var(--ok)' : 'var(--warn)',
                }}
              >
                {learnerCanViewReports ? 'Enabled' : 'Restricted'}
              </span>
              <span style={{ fontSize: '12.5px', color: 'var(--ink)' }}>
                {learnerCanViewReports
                  ? 'Learners can view own scorecards & feedback'
                  : 'Only administrators can view evaluation reports'}
              </span>
            </div>
          </div>

          {/* Mobile Experience Template */}
          <div style={{ background: 'var(--surface)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Mobile UI Template & Footer
            </span>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  background: tenant.governancePolicy?.mobileNavConfig?.showMobileFooter !== false ? '#eff6ff' : '#f1f5f9',
                  color: tenant.governancePolicy?.mobileNavConfig?.showMobileFooter !== false ? '#2563eb' : '#64748b',
                }}
              >
                {tenant.governancePolicy?.mobileNavConfig?.showMobileFooter !== false ? 'Footer Active' : 'Footer Disabled'}
              </span>
              <span style={{ fontSize: '12.5px', color: 'var(--ink)' }}>
                Template: <strong style={{ textTransform: 'capitalize' }}>{tenant.governancePolicy?.mobileNavConfig?.mobileTheme || 'modern-pill'}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <span className="admin-stat-card__label">{t('tenant.stats.users', undefined, 'Users')}</span>
          <span className="admin-stat-card__value">{tenant.userCount ?? users.length}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-card__label">{t('tenant.stats.scenarios', undefined, 'Scenarios')}</span>
          <span className="admin-stat-card__value">{tenant.simulationCount ?? 0}</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-card__label">{t('tenant.stats.attempts_run', undefined, 'Attempts Run')}</span>
          <span className="admin-stat-card__value">{tenant.attemptCount ?? 0}</span>
        </div>
      </div>

      {/* Member Management Section */}
      <div className="admin-section">
        <h2 className="admin-section__title">Organization Members</h2>
        {usersLoading ? (
          <p>Loading members…</p>
        ) : (
          <UserTable
            users={users}
            onRoleChange={async (u, r) => {
              await updateUserRole(u.id, r);
            }}
            onToggleStatus={async (u) => {
              await updateUserStatus(u.id, !u.isActive);
            }}
            canManageUsers={true}
          />
        )}
      </div>

      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '640px' }}>
            <TenantForm
              initialData={{
                name: tenant.name,
                slug: tenant.slug,
                primaryColor: tenant.primaryColor,
                loginLogoUrl: tenant.loginLogoUrl,
                topNavLogoUrl: tenant.topNavLogoUrl,
                directLoginUrl: tenant.directLoginUrl,
                learnerCanViewOwnResultReport: learnerCanViewReports,
                mobileNavConfig: tenant.governancePolicy?.mobileNavConfig,
              }}
              isEdit={true}
              onSubmit={handleEditSubmit}
              onCancel={() => setShowEditModal(false)}
              isLoading={isUpdating}
            />
          </div>
        </div>
      )}
    </div>
  );
}
